import React, { useEffect, useState, useCallback, useRef } from "react";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  TableBody,
  TablePagination,
  IconButton,
  Menu,
} from "@mui/material";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs from "dayjs";

import { useClientInfo, axiosInstance } from "../../utils/commonImports";
import PurchaseFormAdd from "./PurchaseFormAdd";

import { Print as PrintIcon } from "@mui/icons-material";
import PreviewModal from "../../components/preview/PreviewModal";
import { numberToIndianWords } from "../../utils/numberToIndianWords";
import { normalizeDateAny } from "../../utils/dateUtils";

const STATUS_FILTERS = ["All", "Sent", "Printed", "Cancelled", "Deleted"];

export default function PurchaseForm({ initialData }) {
  console.log("Trigger PurchaseForm");
  const tableRef = useRef();
  const { clientId } = useClientInfo();

  const [clientDetails, setClientDetails] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  // Data states
  const [purchases, setPurchases] = useState(initialData || []);

  const [vendors, setVendors] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterPurchaseNumber, setFilterPurchaseNumber] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterVendor, setFilterVendor] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Add/Edit
  // const [showForm, setShowForm] = useState(false); // false OR {data:...}
  const [showForm, setShowForm] = useState(null);
  // null OR { type: "vendor" | "vehicle", data }

  const [anchorElAdd, setAnchorElAdd] = useState(null);

  // Menu + snackbar
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // ---------------------------
  // Dropdown fetch
  // ---------------------------
  const fetchDropdowns = useCallback(async () => {
    try {
      const vendorRes = await axiosInstance.get("/api/vendors", {
        params: { client_id: clientId },
      });
      setVendors(vendorRes.data || []);
    } catch (err) {
      console.error("Dropdown fetch error:", err);
      setSnackbar({
        open: true,
        msg: "Failed to fetch vendors.",
        severity: "error",
      });
    }
  }, [clientId]);

  // ---------------------------
  // Fetch purchases with filters
  // ---------------------------
  const fetchPurchases = useCallback(async () => {
    if (!clientId) return;

    try {
      const params = new URLSearchParams();
      params.append("client_id", clientId);

      if (filterStatus !== "All") params.append("status", filterStatus);
      if (filterPurchaseNumber)
        params.append("purchase_number", filterPurchaseNumber);

      if (filterFromDate)
        params.append("from_date", dayjs(filterFromDate).format("YYYY-MM-DD"));
      if (filterToDate)
        params.append("to_date", dayjs(filterToDate).format("YYYY-MM-DD"));

      if (filterVendor) params.append("vendor_id", filterVendor);

      const res = await axiosInstance.get(
        `/api/purchases?${params.toString()}`,
      );
      setPurchases(res.data || []);

      console.log("PURCHASE API RESULT:", res.data);
    } catch (err) {
      console.error("Fetch purchases error:", err);
      setSnackbar({
        open: true,
        msg: "Failed to fetch purchase records.",
        severity: "error",
      });
    }
  }, [
    clientId,
    filterStatus,
    filterPurchaseNumber,
    filterFromDate,
    filterToDate,
    filterVendor,
  ]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  // useEffect(() => {
  //   fetchPurchases();
  // }, [fetchPurchases]);

  useEffect(() => {
    fetchPurchases();
  }, [
    filterStatus,
    filterPurchaseNumber,
    filterFromDate,
    filterToDate,
    filterVendor,
  ]);

  // ---------------------------
  // Pagination handlers
  // ---------------------------
  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ---------------------------
  // Menu handlers
  // ---------------------------
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // ---------------------------
  // Edit
  // ---------------------------
  const handleEdit = async (row) => {
    handleRowMenuClose();

    try {
      if (!row?.purchaseid) {
        setSnackbar({
          open: true,
          msg: "Invalid purchase selected",
          severity: "error",
        });
        return;
      }

      const res = await axiosInstance.get(`/api/purchases/${row.purchaseid}`, {
        params: { client_id: clientId },
      });

      setShowForm({
        type: res.data?.purchase_type || "vendor",
        data: res.data,
      });
    } catch (err) {
      console.error("Purchase edit fetch error:", err);
      setSnackbar({
        open: true,
        msg: "Failed to load purchase for edit.",
        severity: "error",
      });
    }
  };

  // ---------------------------
  // Delete
  // ---------------------------
  const handleDelete = async (row) => {
    handleRowMenuClose();

    if (!row?.purchaseid) return;

    const ok = window.confirm("Are you sure you want to delete this purchase?");
    if (!ok) return;

    try {
      await axiosInstance.delete(`/api/purchases/${row.purchaseid}`, {
        params: { client_id: clientId },
      });

      setSnackbar({
        open: true,
        msg: "Purchase deleted successfully",
        severity: "success",
      });

      fetchPurchases();
    } catch (err) {
      console.error("Purchase delete error:", err);
      setSnackbar({
        open: true,
        msg: "Delete failed",
        severity: "error",
      });
    }
  };

  function mapTripDuty(t) {
    function showDash(val) {
      if (val === null || val === undefined) return "-";
      if (val === "") return "-";
      if (isNaN(val)) return "-";
      return Number(val);
    }
    function showNumber(val) {
      return val && !isNaN(val) ? Number(val) : 0;
    }
    function showAmount(val) {
      const num = Number(val);
      return num > 0 ? num : "-";
    }
    function formatSlabDisplay(item) {
      const itemDuty = (item.duty || "").trim();
      if (itemDuty === "Outstation" || itemDuty === "OSDayRate") {
        const days = showNumber(item.trip_payouts_days_used);
        const minKm = showNumber(item.trip_payouts_slab_km);
        return days > 0 && minKm > 0 ? `${days} / ${days * minKm}` : "-";
      }
      const hours = showNumber(item.trip_payouts_slab_hour);
      const kms = showNumber(item.trip_payouts_slab_km);
      return hours > 0 || kms > 0 ? `${hours} / ${kms}` : "-";
    }

    let dutyVal = (t.duty || "").trim();
    let batta_local =
      dutyVal === "Local" ? showNumber(t.trip_payouts_local_batta) : 0;
    let batta_os_day =
      dutyVal !== "Local"
        ? showNumber(t.trip_payouts_os_batta_total_amount)
        : 0;
    let batta_os_night =
      dutyVal !== "Local"
        ? showNumber(t.trip_payouts_os_night_batta_total_amount)
        : 0;
    let batta_main = batta_local + batta_os_day + batta_os_night;

    const extra_hour = showNumber(t.trip_payouts_extra_hour);
    const extra_hour_rate = showNumber(t.trip_payouts_slab_extra_hour_rate);
    const extra_km = showNumber(t.trip_payouts_extra_kms);
    const extra_km_rate = showNumber(t.trip_payouts_slab_extra_km_rate);

    const displayExtraHours =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : extra_hour > 0 && extra_hour_rate > 0
          ? `${extra_hour} × ${extra_hour_rate}`
          : "-";
    const displayExtraHoursAmt =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : showAmount(t.trip_payouts_extra_hour_amount);
    const displayExtraKms =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : extra_km > 0 && extra_km_rate > 0
          ? `${extra_km} × ${extra_km_rate}`
          : "-";
    const displayExtraKmsAmt =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : showAmount(t.trip_payouts_extra_km_amount);

    const parkingNum = showNumber(t.trip_payouts_parking);
    const tollNum = showNumber(t.trip_payouts_toll_fasttag);
    const permitNum = showNumber(t.trip_payouts_permit);
    const othersNum = showNumber(t.trip_payouts_others);

    return {
      serial: t.serial,
      duty: t.duty,
      tripsheet_number: t.tripsheet_number,
      open_date: t.open_date
        ? new Date(normalizeDateAny(t.open_date)).toLocaleDateString("en-GB")
        : "-",
      vehicle_no: t.is_vendor_tripsheet
        ? t.vendor_vehicle_no
        : t.vehicle_no || "-",
      vehicle_type: t.assigned_vehicle_group_name || "-",
      days: showDash(t.trip_payouts_days_used),
      hours: showDash(t.trip_payouts_hours_used),
      kms: showDash(t.trip_payouts_kms_used),
      slab_display: formatSlabDisplay(t),
      slab_rate: showNumber(
        dutyVal === "Outstation"
          ? t.trip_payouts_slab_os_rate
          : t.trip_payouts_slab_rate,
      ),
      slab_amount: showNumber(t.trip_payouts_charge_amount),
      extra_hour_amount: showNumber(t.trip_payouts_extra_hour_amount),
      extra_km_amount: showNumber(t.trip_payouts_extra_km_amount),
      extra_hours_display: displayExtraHours,
      extra_hour_amount_display: displayExtraHoursAmt,
      extra_kms_display: displayExtraKms,
      extra_km_amount_display: displayExtraKmsAmt,
      batta_local: showNumber(batta_local),
      batta_os_day: showNumber(batta_os_day),
      batta_os_night: showNumber(batta_os_night),
      nbatta: showNumber(batta_os_night),
      batta_main: showNumber(batta_main),
      parking: parkingNum,
      toll: tollNum,
      permit: permitNum,
      others: othersNum,
      escort_amount: showNumber(t.trip_payouts_escort_amount),
      advance:
        showNumber(t.trip_payouts_advance) +
        showNumber(t.trip_payouts_booking_advance),
      trip_net_amount:
        showNumber(t.trip_payouts_charge_amount) +
        showNumber(t.trip_payouts_extra_hour_amount) +
        showNumber(t.trip_payouts_extra_km_amount) +
        showNumber(batta_main) +
        parkingNum +
        tollNum +
        permitNum +
        othersNum +
        showNumber(t.trip_payouts_escort_amount),
      trip_advance:
        showNumber(t.trip_payouts_advance) +
        showNumber(t.trip_payouts_booking_advance),
    };
  }

  const handlePrint = async (row) => {
  handleRowMenuClose();

  if (!row?.purchaseid) {
    setSnackbar({
      open: true,
      msg: "Invalid purchase selected",
      severity: "error",
    });
    return;
  }

  setPrintLoading(true);
  try {
    // 1) Full purchase record
    const purchaseRes = await axiosInstance.get(
      `/api/purchases/${row.purchaseid}`,
      {
        params: { client_id: clientId },
      },
    );
    const purchase = purchaseRes.data;

    console.log("Print debug — purchase record:", purchase);

    const isVendorPurchase =
      purchase.is_vendor_purchase === true ||
      purchase.is_vendor_purchase === 1;

    // 2) Client details
    let client = clientDetails;
    if (!client?.name) {
      const clientRes = await axiosInstance.get(`/api/clients/${clientId}`);
      client = clientRes.data.client;
      setClientDetails(client);
    }

    // 3) Vendor list (from state or fresh)
    let vendorList = vendors;
    if (!vendorList.length) {
      const vendorRes = await axiosInstance.get("/api/vendors", {
        params: { client_id: clientId },
      });
      vendorList = vendorRes.data || [];
    }

    // 4) Trip duties linked to this purchase (guarded — skip call if ID is missing/empty)
    const filterId = isVendorPurchase
      ? (purchase.vendor_id ?? purchase.vendorid)
      : (purchase.vehicle_id ?? purchase.vehicleid);

    console.log("Print debug — isVendorPurchase:", isVendorPurchase, "resolved filterId:", filterId);

    let selectedArray = [];
    if (filterId !== null && filterId !== undefined && filterId !== "") {
      const tripRes = await axiosInstance.get(
        `/api/purchases/${row.purchaseid}/tripduties`,
        { params: { client_id: clientId, filter_id: filterId } },
      );
      selectedArray = tripRes.data?.selected || [];
    } else {
      console.warn(
        "No valid vendor/vehicle ID found on this purchase — skipping trip duties.",
        purchase,
      );
    }

    const mappedTrips = selectedArray.map((t, i) => {
      const m = mapTripDuty({ ...t, serial: i + 1 });
      return {
        serial: i + 1,
        duty: m.duty || "",
        tripsheet_number: m.tripsheet_number || "",
        open_date: m.open_date || "",
        vehicle_no: t.vehicle_no || t.vehicle_number || m.vehicle_no || "",
        vehicle_type: m.vehicle_type || "",
        days: m.days || 0,
        hours: m.hours || 0,
        kms: m.kms || 0,
        slab_display: m.slab_display || "",
        slab_amount: Number(m.slab_amount || 0),
        extra_hours_display: m.extra_hours_display || 0,
        extra_hour_amount: Number(m.extra_hour_amount || 0),
        extra_kms_display: m.extra_kms_display || 0,
        extra_km_amount: Number(m.extra_km_amount || 0),
        batta_main: Number(m.batta_main || 0),
        parking: Number(m.parking || 0),
        toll: Number(m.toll || 0),
        permit: Number(m.permit || 0),
        others: Number(m.others || 0),
        advance: Number(m.advance || 0),
        trip_net_amount: Number(m.trip_net_amount || 0),
      };
    });

    // 5) Totals
    let basicTotal = 0,
      battaTotal = 0,
      parkingTotal = 0,
      tollTotal = 0,
      permitTotal = 0,
      othersTotal = 0,
      netTotal = 0;

    mappedTrips.forEach((t) => {
      basicTotal += Number(t.slab_amount || 0);
      battaTotal += Number(t.batta_main || 0);
      parkingTotal += Number(t.parking || 0);
      tollTotal += Number(t.toll || 0);
      permitTotal += Number(t.permit || 0);
      othersTotal += Number(t.others || 0);
      netTotal += Number(t.trip_net_amount || 0);
    });

    const totalEarnings =
      basicTotal + battaTotal + parkingTotal + tollTotal + permitTotal + othersTotal;
    const totalDeductions = 0; // matches PurchaseFormAdd — deduction sub-totals are hardcoded to 0 there too

    let vendorDetails = {};
    if (isVendorPurchase) {
      vendorDetails =
        vendorList.find((v) => v.vendorid == purchase.vendor_id) || {};
    } else {
      vendorDetails = {
        vendorname:
          purchase.vehicle_number || mappedTrips?.[0]?.vehicle_no || "",
        vendorgstin: "",
      };
    }

    const netAmount = Number(purchase.net_amount || netTotal);

    const previewObject = {
      client: client || {},
      vendor: {
        vendors: isVendorPurchase
          ? vendorDetails.vendorname || ""
          : purchase.vehicle_number || mappedTrips?.[0]?.vehicle_no || "",
        vendorgstin: isVendorPurchase ? vendorDetails.vendorgstin || "" : "",
      },
      purchase: {
        number: purchase.purchase_number,
        date: purchase.purchase_date
          ? new Date(purchase.purchase_date).toLocaleDateString("en-GB")
          : "",
        period_from: purchase.period_from || "",
        period_to: purchase.period_to || "",
      },
      trips: mappedTrips,
      totals: {
        net: netAmount.toFixed(2),
        word_amount: numberToIndianWords(netAmount),
      },
      totalsBreakup: {
        basic: basicTotal.toFixed(2),
        batta: battaTotal.toFixed(2),
        parking: parkingTotal.toFixed(2),
        toll: tollTotal.toFixed(2),
        others: othersTotal.toFixed(2),
        net: netAmount.toFixed(2),
      },
      terms: [],
      totalsSummary: {
        earnings: totalEarnings.toFixed(2),
        deductions: totalDeductions.toFixed(2),
      },
    };

    console.log("Print debug — previewObject:", previewObject);

    const previewRes = await axiosInstance.post(
      "/api/purchase-template/preview/compressed",
      previewObject,
    );

    setPreviewHTML(previewRes.data.html);
    setPreviewData(previewObject);
    setPreviewOpen(true);
  } catch (err) {
    console.error("Print preview failed:", err.response || err);
    setSnackbar({
      open: true,
      msg: `Failed to build print preview: ${
        err.response?.data?.message || err.message || "Unknown error"
      }`,
      severity: "error",
    });
  } finally {
    setPrintLoading(false);
  }
};
// ---------------------------
  // Reset filters
  // ---------------------------
  const resetFilters = () => {
    setFilterStatus("All");
    setFilterPurchaseNumber("");
    setFilterFromDate("");
    setFilterToDate("");
    setFilterVendor("");
  };

  // Paginate
  const pagedRows = purchases.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // UI compact
  const compactStyle = {
    minWidth: 140,
    height: 36,
    "& .MuiInputBase-root": {
      height: 36,
      fontSize: "0.75rem",
      padding: "2px 8px",
    },
  };

  const compactSelectStyle = {
    ...compactStyle,
    minWidth: 180,
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 100px)",
        p: 1,
        bgcolor: "#f8fafc",
      }}
    >
      {/* FILTER BAR */}
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            mb: 1,
            alignItems: "center",
          }}
        >
          {/* Status */}
          <FormControl size="small" sx={compactStyle}>
            <InputLabel sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
              Status
            </InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.75rem" } } }}
              sx={{ height: 36, fontSize: "0.75rem" }}
            >
              {STATUS_FILTERS.map((st) => (
                <MenuItem key={st} value={st} sx={{ fontSize: "0.75rem" }}>
                  {st}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Purchase No */}
          <TextField
            size="small"
            label="Purchase No"
            value={filterPurchaseNumber}
            onChange={(e) => setFilterPurchaseNumber(e.target.value)}
            sx={compactStyle}
          />

          {/* Vendor */}
          <FormControl
            size="small"
            sx={{ ...compactSelectStyle, minWidth: 380 }}
          >
            <InputLabel sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
              Vendor
            </InputLabel>
            <Select
              value={filterVendor}
              label="Vendor"
              onChange={(e) => setFilterVendor(e.target.value)}
              sx={{ height: 36, fontSize: "0.75rem" }}
            >
              <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                All
              </MenuItem>
              {vendors.map((v) => (
                <MenuItem
                  key={v.vendorid}
                  value={v.vendorid}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {v.vendorname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* From Date */}
          <TextField
            label="From Date"
            type="date"
            value={filterFromDate || ""}
            onChange={(e) => setFilterFromDate(e.target.value)}
            size="small"
            sx={{ ...compactStyle, width: 140 }}
            InputLabelProps={{ shrink: true }}
          />

          {/* To Date */}
          <TextField
            label="To Date"
            type="date"
            value={filterToDate || ""}
            onChange={(e) => setFilterToDate(e.target.value)}
            size="small"
            sx={{ ...compactStyle, width: 140 }}
            InputLabelProps={{ shrink: true }}
          />

          {/* Reset */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={resetFilters}
            sx={{
              minWidth: 110,
              fontSize: "0.75rem",
              height: 32,
              padding: "0 12px",
              textTransform: "none",
            }}
          >
            RESET
          </Button>

          {/* ADD */}
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => setAnchorElAdd(e.currentTarget)}
              sx={{
                minWidth: 100,
                fontSize: "0.75rem",
                height: 32,
                padding: "0 8px",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              ADD
              <ArrowDropDownIcon sx={{ fontSize: 18, ml: 0.5 }} />
            </Button>

            <Menu
              anchorEl={anchorElAdd}
              open={Boolean(anchorElAdd)}
              onClose={() => setAnchorElAdd(null)}
            >
              <MenuItem
                onClick={() => {
                  setAnchorElAdd(null);
                  setShowForm({ type: "vendor", data: null });
                }}
                sx={{ fontSize: "0.75rem" }}
              >
                Vendor Purchase
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorElAdd(null);
                  setShowForm({ type: "vehicle", data: null });
                }}
                sx={{ fontSize: "0.75rem" }}
              >
                Vehicle Purchase
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}

      {/* FORM */}
      {showForm ? (
        <PurchaseFormAdd
          purchaseType={showForm.type} // ✅ vendor | vehicle
          editData={showForm.data}
          onCancel={() => setShowForm(null)}
          onSuccess={() => {
            setShowForm(null);
            setSnackbar({
              open: true,
              msg: "Purchase saved successfully",
              severity: "success",
            });
            fetchPurchases();
          }}
        />
      ) : (
        <Paper
          elevation={2}
          sx={{ borderRadius: 2, overflowX: "auto" }}
          ref={tableRef}
        >
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5", height: 36 }}>
                <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                  Purchase No
                </TableCell>
                <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                  Vendor
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Basic
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  GST
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Net Amount
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "center",
                    width: 80,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.length > 0 ? (
                pagedRows.map((row) => (
                  <TableRow key={row.purchaseid} hover>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.purchase_number || "-"}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.purchase_date
                        ? dayjs(row.purchase_date).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.display_name || "-"}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {Number(row.basic_amount || 0).toFixed(2)}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {(
                        Number(row.igst_amount || 0) +
                        Number(row.cgst_amount || 0) +
                        Number(row.sgst_amount || 0)
                      ).toFixed(2)}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {Number(row.net_amount || 0).toFixed(2)}
                    </TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleRowMenuOpen(e, row)}
                        title="Actions"
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No purchases to display.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={purchases.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page"
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${from}-${to} of ${count}`
            }
            sx={{
              ".MuiTablePagination-toolbar": { minHeight: 36, height: 36 },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: 12,
                },
            }}
          />
        </Paper>
      )}

      {/* ACTION MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRowMenuClose}
      >
        <MenuItem onClick={() => handleEdit(menuRow)} sx={{ fontSize: 13 }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handlePrint(menuRow)} sx={{ fontSize: 13 }}>
          <PrintIcon fontSize="small" sx={{ mr: 1 }} /> Print
        </MenuItem>

        <MenuItem
          onClick={() => handleDelete(menuRow)}
          sx={{ fontSize: 13, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontSize: "0.95rem",
            fontWeight: 500,
            borderRadius: "8px",
            boxShadow: "0px 3px 8px rgba(0,0,0,0.25)",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            ...(snackbar.severity === "error" && {
              backgroundColor: "#fc0707ff",
            }),
            ...(snackbar.severity === "success" && {
              backgroundColor: "#04710cff",
            }),
          }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
      {previewOpen && previewHTML && (
        <PreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          html={previewHTML}
          invoiceData={previewData}
          mode="compressed"
          templateType="purchase"
          showSnackbar={(snack) =>
            setSnackbar({
              open: true,
              msg: snack.message,
              severity: snack.severity,
            })
          }
        />
      )}
    </Box>
  );
}
